import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, NgForm, Validators } from '@angular/forms';
import { DateAdapter, MatSnackBar, MAT_DATE_FORMATS, ThemePalette } from '@angular/material';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap, tap } from 'rxjs/operators';
import { AppDateAdapter, APP_DATE_FORMATS } from '../../../core/format-datepicker';
import { CodeTable } from '../../../core/model/v2/code-table';
import { PastHealthHistory, PastHealthHistoryObj } from '../../../core/models/patient/PastHealthHostoryQuestions';
import { Patient } from '../../../core/models/patient/patient';
import { MedicineModel, PatientHealthHistory } from '../../../core/models/patient/PatientHealthHistory';
import { patientHealthSubType, patientHealthType } from '../../../core/models/patient/patientHealthType';
import { CommonService } from '../../../core/services/v2/common.service';
import { PatientService } from '../../../core/services/v2/patient.service';
import { WindowLoaderService } from '../../../shared/components/window-loader/window-loader.service';


@Component({
  selector: 'app-patient-medication',
  templateUrl: './patient-medication.component.html',
  styleUrls: ['./patient-medication.component.scss'],
  providers: [
    { provide: DateAdapter, useClass: AppDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS }]
})
export class PatientMedicationComponent implements OnInit, OnChanges {
  @Input() editEnable: boolean;
  @Input() patientProfile: Patient;
  @Input() patientHealthHistories: PatientHealthHistory[];
  @Input() patientHealthTypes: patientHealthType[];
  @Input() healthHistoryQuestions: PastHealthHistory[];
  @ViewChild('#medicationHistoryForm', null) medicationHistoryForm: NgForm;
  pastHistoryData: PastHealthHistory[];

  patientHealthSubTypes: patientHealthSubType[];
  pastHealthHistoryObj: PastHealthHistoryObj[];
  prescribedForm: FormGroup;
  vitaminsForm: FormGroup;
  overTheCounterForm: FormGroup;
  isUpdate: boolean = false;
  updatedItemIndex: number;
  professionalFilteredOptions: Observable<any[]>;
  pharmacyFilteredOptions: Observable<any[]>;
  diagnosisCodeFilteredOptions: Observable<any[]>;
  medicineNameFilteredOptions: Observable<any[]>;
  supplementNameFilteredOptions: Observable<any[]>;
  medicationAutocomleteApis = {
    'professional': 'Practitioner/GetPractitionersByName?searchText=',
    'pharmacy': 'Common/GetPharmacyDetailsByZipCode?pharmacyName=',
    'medicineName': 'Medicine/GetByName?name={searchText}&isSupplement=false',
    'diagnosisCode': 'Practitioner/GetSymptomsByName?name=',
    'supplementName': 'Medicine/GetByName?name={searchText}&isSupplement=true'
  };
  maxDate: Date = new Date();
  todayDate: Date = new Date();
  myControl = new FormControl();
  medicineFormCodes: CodeTable;
  question: string = 'Would you like to add ';
  newMedicineOrSupplimentAdded: boolean = false;

  constructor(private patientService: PatientService,
    private fb: FormBuilder,
    private el: ElementRef,
    private loaderService: WindowLoaderService,
    private _snackBar: MatSnackBar,
    private commonService: CommonService) { }

  ngOnInit(): void {
    this.prescribedForm = this.fb.group({
      id: [],
      professional: new FormControl('', Validators.required),
      npi: [{ value: '', disabled: true }],
      speciality: [{ value: '', disabled: true }],
      pharmacy: [''],
      medicineName: ['', Validators.required],
      dosage: ['', Validators.required],
      dosageUnit: ['', Validators.required],
      form: ['', Validators.required],
      diagnosisCode: ['', Validators.required],
      description: [{ value: ''}],
      dosageInstruction: [''],
      frequency: ['', Validators.required],
      // manufacturer: ['', Validators.required],
      pharmacyRxNum: [''],
      // servingPerBottle: [''],
      refillCount: [''],
      refillBeforeDate: ['', Validators.required],
      lastDateFilled: ['', Validators.required],
      quantity: [{ value: ''}]
    });
    this.vitaminsForm = this.fb.group({
      id: [],
      manufacturer: ['', Validators.required],
      supplementName: ['', Validators.required],
      dosage: ['', Validators.required],
      form: ['', Validators.required],
      dosageInstruction: [''],
      frequency: ['', Validators.required],
      servingPerBottle: [''],
      bottlesOnHand: ['']
    });
    this.overTheCounterForm = this.fb.group({
      id: [],
      medicineName: ['', Validators.required],
      quantity: ['', Validators.required],
      dateOfPurchase: ['', Validators.required]
    });
    this.bindAutocompletes();
  }

  loadCodes() {
    this.commonService.GetItemsByCodeNamesWithoutCache(['MEDICINEFORM']).subscribe(
      codeTables => {
        this.medicineFormCodes = codeTables.codeTable[0];
      },
      error => console.log(error)
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.healthHistoryQuestions && this.healthHistoryQuestions.length > 0) {
      this.pastHistoryData = this.healthHistoryQuestions.filter(x => x.typeId == 6);
      if (this.patientHealthTypes && this.patientHealthHistories) {
        this.patientHealthSubTypes = this.patientHealthTypes.find(x => x.id == 6).healthSubTypes;
        this.mapPastHealthHistoryObj();
        this.loadCodes();
      }
    }
  }

  mapPastHealthHistoryObj() {
    this.pastHealthHistoryObj = [];
    for (let value of this.patientHealthSubTypes) {
      let data = null;
      let patientHealthHistoryId: number = 0;
      if (this.patientHealthHistories && this.patientHealthHistories.length > 0) {
        const patientHeathHisory = this.patientHealthHistories.find(x => x.typeId == value.typeId && x.subTypeId == value.id);
        if (patientHeathHisory) {
          patientHealthHistoryId = patientHeathHisory.id;
          const dataString = patientHeathHisory.healthData;
          if (dataString && dataString != '') {
            data = JSON.parse(dataString);
          }
        }
      }
      let dataAvailable = "No";
      if (patientHealthHistoryId > 0) {
        dataAvailable = "Yes";
      }
      this.pastHealthHistoryObj.push({ patientHealthHistoryId: patientHealthHistoryId, dataAvailable: dataAvailable, typeId: value.typeId, subTypeId: value.id, subTypeName: value.name, healthData: data, showData: false, dataCount: 0 });
    }
    this.checkForPrescribedInActiveData();
  }

  checkForPrescribedInActiveData() {
    const prescribedActiveData = this.pastHealthHistoryObj.find(x => x.subTypeId == 36).healthData;
    let prescribedInactiveActiveData = [];
    if (prescribedActiveData) {
      for (let item of prescribedActiveData) {
        if (item.refillBeforeDate && item.refillBeforeDate != '') {
          item.refillBeforeDate = new Date(item.refillBeforeDate);
          if (item.refillBeforeDate.setHours(0, 0, 0, 0) < this.todayDate.setHours(0, 0, 0, 0)) {
            prescribedInactiveActiveData.push(item);
          }
        }
        else {
          prescribedInactiveActiveData.push(item);
        }
      }
      if (prescribedInactiveActiveData.length > 0) {
        const indexPrescribedInactive = this.pastHealthHistoryObj.findIndex(x => x.subTypeId == 37);
        this.pastHealthHistoryObj[indexPrescribedInactive].healthData = prescribedInactiveActiveData;
        this.pastHealthHistoryObj[indexPrescribedInactive].dataAvailable = 'Yes';
      }
    }
  }

  toggleForm(index: number) {
    this.prescribedForm.reset();
    this.vitaminsForm.reset();
    this.overTheCounterForm.reset();
    let data = this.pastHealthHistoryObj;
    this.pastHealthHistoryObj[index].showData = !data[index].showData;
  }

  toggleCard(collapseCard: HTMLElement) {
    if (collapseCard.classList.contains('active')) {
      collapseCard.classList.remove('active');
    }
    else {
      collapseCard.classList.add('active');
    }
  }

  bindAutocompletes() {
    this.professionalFilteredOptions = this.prescribedForm.controls.professional.valueChanges.pipe(
      startWith(''),
      debounceTime(400),
      distinctUntilChanged(),
      switchMap((val) => {
        if (!val || val === '' || val.length < 3) {
          return new Observable<any[]>();
        }
        return this.filter(val || '', this.medicationAutocomleteApis.professional);
      })
    );
    this.pharmacyFilteredOptions = this.prescribedForm.controls.pharmacy.valueChanges.pipe(
      startWith(''),
      debounceTime(400),
      distinctUntilChanged(),
      switchMap((val) => {
        if (!val || val === '' || val.length < 3) {
          return new Observable<any[]>();
        }
        return this.filterPharmacy(val || '', this.medicationAutocomleteApis.pharmacy);
      })
    );
    this.diagnosisCodeFilteredOptions = this.prescribedForm.controls.diagnosisCode.valueChanges.pipe(
      startWith(''),
      debounceTime(400),
      distinctUntilChanged(),
      switchMap((val) => {
        if (!val || val === '' || val.length < 3) {
          return new Observable<any[]>();
        }
        return this.filterDiagnosisCode(val || '', this.medicationAutocomleteApis.diagnosisCode);
      })
    );
    this.medicineNameFilteredOptions = this.prescribedForm.controls.medicineName.valueChanges.pipe(
      startWith(''),
      debounceTime(400),
      distinctUntilChanged(),
      switchMap((val) => {
        if (!val || val === '' || val.length < 3) {
          return new Observable<any[]>();
        }
        return this.filterMedicineOrSupplimentName(val || '', this.medicationAutocomleteApis.medicineName);
      })
    );
    this.supplementNameFilteredOptions = this.vitaminsForm.controls.supplementName.valueChanges.pipe(
      startWith(''),
      debounceTime(400),
      distinctUntilChanged(),
      switchMap((val) => {
        if (!val || val === '' || val.length < 3) {
          return new Observable<any[]>();
        }
        return this.filterMedicineOrSupplimentName(val || '', this.medicationAutocomleteApis.supplementName);
      })
    );

  }

  private filter(value: string, apiUrl: string): Observable<any[]> {
    apiUrl += value;
    return this.commonService.getDynamicSearchApiData(apiUrl).pipe(
      tap(),
      map((response) => response.practitioners)
    );
  }

  private filterPharmacy(value: string, apiUrl: string): Observable<any[]> {
    apiUrl += value;
    return this.commonService.getDynamicSearchApiData(apiUrl).pipe(
      tap(),
      map((response) => response.pharmacyDetails)
    );
  }

  private filterDiagnosisCode(value: string, apiUrl: string): Observable<any[]> {
    apiUrl += value;
    return this.commonService.getDynamicSearchApiData(apiUrl).pipe(
      tap(),
      map((response) => response)
    );
  }

  private filterMedicineOrSupplimentName(value: string, apiUrl: string): Observable<any[]> {
    apiUrl = apiUrl.replace('{searchText}', value);
    return this.commonService.getDynamicSearchApiData(apiUrl).pipe(
      tap(),
      map((response) => response.length > 0 ? response : [{ name: `${this.question}"${value}"`, value: value }])
    );
  }

  optionProfessionalSelected(option) {
    if (option.value) {
      this.prescribedForm.controls.professional.setValue(option.value.practitionerName);
      this.prescribedForm.controls.speciality.setValue(option.value.specialty);
      this.prescribedForm.controls.npi.setValue(option.value.practitionerNPI);
    }
  }
  optionDiagnosisSelected(option) {
    if (option.value) {
      this.prescribedForm.controls.diagnosisCode.setValue(option.value.name);
      // this.prescribedForm.controls.diagnosisCode.setValue(option.value.code);
      this.prescribedForm.controls.description.setValue(option.value.name);
    }
  }
  optionMedicineNameSelected(option) {
    if (option.value) {
      this.prescribedForm.controls.dosage.enable();
      this.prescribedForm.controls.dosage.setValue('');
      this.newMedicineOrSupplimentAdded = false;
      if (option.value.value) {
        this.prescribedForm.controls.medicineName.setValue(option.value.value);
        this.newMedicineOrSupplimentAdded = true;
      }
      else {
        this.prescribedForm.controls.medicineName.setValue(option.value.name);
        this.prescribedForm.controls.dosage.setValue(option.value.dosage);
        this.prescribedForm.controls.dosage.disable();
      }
    }
  }

  optionSupplementNameSelected(option) {
    if (option.value) {
      this.vitaminsForm.controls.dosage.enable();
      this.vitaminsForm.controls.dosage.setValue('');
      this.newMedicineOrSupplimentAdded = false;
      if (option.value.value) {
        this.vitaminsForm.controls.supplementName.setValue(option.value.value);
        this.newMedicineOrSupplimentAdded = true;
      }
      else {
        this.vitaminsForm.controls.supplementName.setValue(option.value.name);
        this.vitaminsForm.controls.dosage.setValue(option.value.dosage);
        this.vitaminsForm.controls.dosage.disable();
      }
    }
  }

  getMedicineFromFilteredOptions(name: string): Observable<any> {
    return this.medicineNameFilteredOptions.pipe(
      map(opt => opt.find(item => item.name === name))
    );
  }

  submitPrescribedActive(subTypeIndex: number) {
    const validate = this.validateForm(this.prescribedForm);
    if (!validate) {
      return;
    }
    const formObj = this.prescribedForm.getRawValue();
    this.saveMedicationData(subTypeIndex, formObj, 'Medicine');
  }
  submitVitamin(subTypeIndex: number) {
    const validate = this.validateForm(this.vitaminsForm);
    if (!validate) {
      return;
    }
    const formObj = this.vitaminsForm.getRawValue();
    this.saveMedicationData(subTypeIndex, formObj, 'Supplement');
  }
  submitOverTheCounter(subTypeIndex: number) {
    const validate = this.validateForm(this.overTheCounterForm);
    if (!validate) {
      return;
    }
    const formObj = this.overTheCounterForm.getRawValue();
    this.saveMedicationData(subTypeIndex, formObj, '');
  }

  validateForm(addedForm: FormGroup) {
    const validationMessages = {
      'professional': 'Prescribing Healthcare Professional is required.',
      'medicineName': 'Medication Name is required.',
      'dosage': 'Dosage is required.',
      'form': 'Form is required.',
      'diagnosisCode': 'Diagnosis Code is required.',
      'manufacturer': 'Manufacturer Name is required.',
      'frequency': 'Frequency is required.',
      'supplementName': 'Supplement Name is required.',
      'quantity': 'Quantity is required.',
      'dateOfPurchase': 'Date Of Purchase is required.',
      'refillBeforeDate': 'Refill Before Date is required.',
    };
    const formControls = addedForm.controls;
    const invalidControls = [];
    for (const name in formControls) {
      if (formControls[name].invalid) {
        invalidControls.push(name);
      }
    }
    if (invalidControls.length > 0) {
      const controlName = invalidControls[0];
      this._snackBar.open(validationMessages[controlName], 'Ok', { duration: 5 * 1000 });
      const invalidControl = this.el.nativeElement.querySelector('[formcontrolname="' + controlName + '"]');
      if (invalidControl.nodeName == 'APP-DYNAMIC-AUTOCOMPLETE') {
        invalidControl.childNodes[0].focus();
      }
      else {
        invalidControl.focus();
      }
      return false;
    }
    else {
      return true;
    }
  }

  saveMedicationData(subTypeIndex: number, formObj: any, service: string) {
    let healthHistory = this.pastHealthHistoryObj[subTypeIndex];
    if (!formObj) {
      this._snackBar.open('Data can not be saved.', 'Ok', { duration: 5 * 1000 });
    }
    healthHistory = this.updateExistingData(healthHistory, formObj);
    const healthData = JSON.stringify(healthHistory.healthData);
    if (!healthData) {
      this._snackBar.open('Data can not be saved.', 'Ok', { duration: 5 * 1000 });
    }
    const PatientHealthHistoryData: PatientHealthHistory[] = [{
      id: healthHistory.patientHealthHistoryId,
      patientId: this.patientProfile.id,
      typeId: healthHistory.typeId,
      subTypeId: healthHistory.subTypeId,
      healthData: healthData,
      subTypeName: null,
      typeName: null,
      active: true
    }];
    this.loaderService.blockWithLoader(this.patientService.savePatientHealthHistoryData(PatientHealthHistoryData)).subscribe(
      (details) => {
        if (details.isSuccess) {
          let data = this.pastHealthHistoryObj[subTypeIndex];
          data.dataAvailable = "Yes";
          this.pastHealthHistoryObj[subTypeIndex] = data;
          this.toggleForm(subTypeIndex);
          this.isUpdate = false;
          this.updatedItemIndex = null;
          if (this.newMedicineOrSupplimentAdded && service == 'Medicine') {
            this.saveNewMedicineOrSupplement(formObj.medicineName, formObj.dosage, false);
          }
          else if (this.newMedicineOrSupplimentAdded && service == 'Supplement') {
            this.saveNewMedicineOrSupplement(formObj.supplementName, formObj.dosage, true);
          }
        }
      },
      (error) => { console.error(error); }
    );
  }

  saveNewMedicineOrSupplement(medicineName: string, dosage: string, isSupplement: boolean) {
    const data: MedicineModel = {
      name: medicineName,
      dosage: dosage,
      isSupplement: isSupplement
    };
    this.patientService.CreateMedicineOrSuppliment(data).subscribe(
      (details) => {
      },
      (error) => { console.error(error); }
    );
  }

  updateExistingData(healthHistory: PastHealthHistoryObj, addedData: any) {
    if (!this.isUpdate) {
      addedData.id = Math.floor(Math.random() * (999 - 100 + 1) + 100);
      if (healthHistory.healthData && healthHistory.healthData.length > 0) {
        healthHistory.healthData.push(addedData);
      }
      else {
        healthHistory.healthData = [];
        healthHistory.healthData.push(addedData);
      }
    }
    else {
      if (this.updatedItemIndex > -1) {
        healthHistory.healthData[this.updatedItemIndex] = addedData;
      }
    }
    return healthHistory;
  }

  editData(item: any, index: number, subIndex: number, subTypeId: number) {
    this.toggleForm(subIndex);
    if (subTypeId === 36) {
      this.prescribedForm.patchValue(item);
    }
    else if (subTypeId === 38) {
      this.vitaminsForm.patchValue(item);
    }
    else if (subTypeId === 39) {

    }
    this.isUpdate = true;
    this.updatedItemIndex = index;
  }


}
